import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  signal
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { I18nService } from 'i18n';
import { FoundingMemberBadgeComponent } from 'shared';
import { CommunityEditorComponent } from '../community-editor/community-editor.component';
import { CommunityHistoryComponent } from '../community-history/community-history.component';
import { CommunityBadgesComponent } from '../community-badges/community-badges.component';
import { CommunityReactionsComponent } from '../community-reactions/community-reactions.component';
import { CommunityAvatarComponent } from '../community-avatar/community-avatar.component';
import { CommunityTranslateComponent } from '../community-translate/community-translate.component';
import { CommunityPostNode, communityBodyBlank } from '../../models/community.models';

export interface PostReplyPayload {
  parentPostId: number;
  body: string;
}

export interface PostEditPayload {
  postId: number;
  body: string;
}

/** Mute/unmute request for an author; the page asks for the mandatory reason. */
export interface PostHideUserPayload {
  userId: number;
  hide: boolean;
  name: string;
}

@Component({
  selector: 'app-community-post',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    TranslocoModule,
    FoundingMemberBadgeComponent,
    CommunityEditorComponent,
    CommunityHistoryComponent,
    CommunityBadgesComponent,
    CommunityReactionsComponent,
    CommunityAvatarComponent,
    CommunityTranslateComponent
  ],
  templateUrl: './community-post.component.html',
  styleUrl: './community-post.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.community-post--deleted]': 'displayStatus === "deleted"',
    '[class.community-post--hidden]': 'displayStatus === "hidden" || authorHidden'
  }
})
export class CommunityPostComponent {
  @Input({ required: true }) post!: CommunityPostNode;
  @Input() threadLocked = false;
  /**
   * Whether the viewer may comment in this thread at all. Set from the
   * thread's `can_reply`; falls back to the locked check on backends that
   * don't send it.
   */
  @Input() canReply = true;
  /** The channel's reaction set, passed down from the thread page. */
  @Input() reactionSet: string[] | null = null;
  @Input() depth = 0;
  /** Q&A (§17): whether the viewer (asker or a moderator) may mark/unmark a best answer here. */
  @Input() canSolve = false;
  /** Q&A: the thread's current best-answer post id, if any. */
  @Input() solvedPostId: number | null = null;
  /** Whether the network has a translation provider configured (§15, F10). */
  @Input() translationAvailable = false;
  /** Passed straight through to the reply/edit editors (mentions, uploads, video embed). */
  @Input() channelId: number | null = null;
  @Input() canMention = false;
  @Input() canUploadImage = false;
  @Input() canUploadFile = false;
  @Input() canEmbedVideo = false;

  @Output() reply = new EventEmitter<PostReplyPayload>();
  @Output() edit = new EventEmitter<PostEditPayload>();
  @Output() delete = new EventEmitter<number>();
  @Output() toggleHide = new EventEmitter<{ postId: number; hide: boolean }>();
  @Output() toggleHideUser = new EventEmitter<PostHideUserPayload>();
  /** Translation key of a failed inline action (reaction toggle), for the page's error alert. */
  @Output() actionFailed = new EventEmitter<string>();
  /** The viewer wants to report this post (the page opens the reason dialog). */
  @Output() report = new EventEmitter<number>();
  /** A moderator wants the author's moderation summary. */
  @Output() memberSummary = new EventEmitter<number>();
  /** Mark this post as the thread's best answer. */
  @Output() markSolved = new EventEmitter<number>();
  /** Unmark the thread's current best answer (emitted from any post in the tree). */
  @Output() unmarkSolved = new EventEmitter<void>();
  /** Translation key of a failed editor upload/embed/mention request. */
  @Output() uploadError = new EventEmitter<string>();

  replyOpen = signal(false);
  editOpen = signal(false);
  historyOpen = signal(false);
  replyDraft = '';
  editDraft = '';

  toggleHistory(): void {
    this.historyOpen.update(v => !v);
  }

  constructor(private i18n: I18nService) {}

  /** Public member profile of the author. */
  get authorLink(): string | null {
    const id = this.post.user?.id;
    return id ? `/${this.i18n.getLanguage()}/community/u/${id}` : null;
  }

  /** Pending/rejected badge: for the author themselves and for moderators. */
  get approvalBadge(): 'pending' | 'rejected' | null {
    const status = this.post.approval_status;
    if (status !== 'pending' && status !== 'rejected') return null;
    return this.post.is_mine || this.canModerate ? status : null;
  }

  /** Reporting needs visible, live content by someone else. */
  get canReport(): boolean {
    return !this.post.is_mine && !this.post.masked && !this.post.deleted_at && !this.post.hidden_at;
  }

  /** Q&A: whether this post is the thread's current best answer. */
  get isAcceptedAnswer(): boolean {
    return this.solvedPostId != null && this.solvedPostId === this.post.id;
  }

  get isDeletedOrHidden(): boolean {
    return this.post.masked || !!this.post.deleted_at || !!this.post.hidden_at;
  }

  get displayStatus(): 'deleted' | 'hidden' | 'visible' {
    if (this.post.hidden_at) return 'hidden';
    if (this.post.deleted_at) return 'deleted';
    return 'visible';
  }

  /** Admin view: the author is muted, so members see nothing of this post. */
  get authorHidden(): boolean {
    return !!this.post.author_hidden || !!this.post.user?.community_hidden;
  }

  /** Moderation rights on the channel; `can_admin` is the pre-Phase-0 alias. */
  get canModerate(): boolean {
    return this.post.can_moderate ?? this.post.can_admin ?? false;
  }

  /** Falls back to `can_modify` for backends that predate the separate flag. */
  get canDelete(): boolean {
    return this.post.can_delete ?? this.post.can_modify;
  }

  get canHideAuthor(): boolean {
    return this.canModerate && !!this.post.user?.can_hide;
  }

  openReply(): void {
    this.replyDraft = '';
    this.replyOpen.set(true);
  }

  cancelReply(): void {
    this.replyOpen.set(false);
  }

  /** A body of nothing but empty lines is not a reply — the backend refuses it too. */
  bodyBlank(html: string): boolean {
    return communityBodyBlank(html);
  }

  submitReply(): void {
    const body = (this.replyDraft || '').trim();
    if (communityBodyBlank(body)) return;
    this.reply.emit({ parentPostId: this.post.id, body });
    this.replyOpen.set(false);
    this.replyDraft = '';
  }

  openEdit(): void {
    this.editDraft = this.post.body || '';
    this.editOpen.set(true);
  }

  cancelEdit(): void {
    this.editOpen.set(false);
  }

  submitEdit(): void {
    const body = (this.editDraft || '').trim();
    if (communityBodyBlank(body)) return;
    this.edit.emit({ postId: this.post.id, body });
    this.editOpen.set(false);
  }

  onDelete(): void {
    this.delete.emit(this.post.id);
  }

  onHide(hide: boolean): void {
    this.toggleHide.emit({ postId: this.post.id, hide });
  }

  onHideUser(hide: boolean): void {
    const userId = this.post.user?.id;
    if (!userId) return;
    this.toggleHideUser.emit({ userId, hide, name: this.post.user?.display_name || '' });
  }

  onReport(): void {
    this.report.emit(this.post.id);
  }

  onMemberSummary(): void {
    const userId = this.post.user?.id;
    if (userId) this.memberSummary.emit(userId);
  }

  onMarkSolved(): void {
    this.markSolved.emit(this.post.id);
  }

  onUnmarkSolved(): void {
    this.unmarkSolved.emit();
  }

  // re-emitters for recursive children
  onChildReply(p: PostReplyPayload): void { this.reply.emit(p); }
  onChildEdit(p: PostEditPayload): void { this.edit.emit(p); }
  onChildDelete(id: number): void { this.delete.emit(id); }
  onChildToggleHide(p: { postId: number; hide: boolean }): void { this.toggleHide.emit(p); }
  onChildToggleHideUser(p: PostHideUserPayload): void { this.toggleHideUser.emit(p); }
  onChildActionFailed(key: string): void { this.actionFailed.emit(key); }
  onChildReport(id: number): void { this.report.emit(id); }
  onChildMemberSummary(id: number): void { this.memberSummary.emit(id); }
  onChildMarkSolved(id: number): void { this.markSolved.emit(id); }
  onChildUnmarkSolved(): void { this.unmarkSolved.emit(); }
  onChildUploadError(key: string): void { this.uploadError.emit(key); }
}
